"use client";
import {
  Dropzone,
  DropZoneArea,
  DropzoneDescription,
  DropzoneFileList,
  DropzoneFileListItem,
  DropzoneMessage,
  DropzoneRemoveFile,
  DropzoneTrigger,
  useDropzone,
} from "@/components/ui/dropzone";
import { Trash2Icon, UploadCloud } from "lucide-react";
import { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FileText, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";

export function MultiImages({ placeholder = "Please select up to 10 images" }) {
  const dropzone = useDropzone({
    onDropFile: async (file) => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return {
        status: "success",
        result: URL.createObjectURL(file),
      };
    },
    validation: {
      accept: {
        "image/*": [".png", ".jpg", ".jpeg"],
      },
      maxSize: 10 * 1024 * 1024,
      maxFiles: 10,
    },
  });

  return (
    <div className="not-prose flex flex-col gap-4">
      <Dropzone {...dropzone}>
        <div>
          <div className="flex justify-between">
            <DropzoneDescription>{placeholder}</DropzoneDescription>
            <DropzoneMessage />
          </div>
          <DropZoneArea>
            <DropzoneTrigger className="flex flex-col items-center gap-4 bg-transparent p-10 text-center text-sm">
              <UploadCloud className="size-8" />
              <div>
                <p className="font-semibold">Upload listing images</p>
                <p className="text-sm text-muted-foreground">
                  Click here or drag and drop to upload
                </p>
              </div>
            </DropzoneTrigger>
          </DropZoneArea>
        </div>

        <DropzoneFileList className="grid grid-cols-3 gap-3 p-0">
          {dropzone.fileStatuses.map((file) => (
            <DropzoneFileListItem
              className="overflow-hidden rounded-md bg-secondary p-0 shadow-sm"
              key={file.id}
              file={file}
            >
              {file.status === "pending" && (
                <div className="aspect-video animate-pulse bg-brand-100/20" />
              )}
              {file.status === "success" && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={file.result}
                  alt={`uploaded-${file.fileName}`}
                  className="aspect-video object-cover"
                />
              )}
              <div className="flex items-center justify-between p-2 pl-4">
                <div className="min-w-0">
                  <p className="truncate text-sm">{file.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {(file.file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
                <DropzoneRemoveFile
                  variant="ghost"
                  className="shrink-0 hover:outline"
                >
                  <Trash2Icon className="size-4" />
                </DropzoneRemoveFile>
              </div>
            </DropzoneFileListItem>
          ))}
        </DropzoneFileList>
      </Dropzone>
    </div>
  );
}

export function SingleImages({
  placeholder,
  fileFormat,
  value,
  onChange,
  error,
}) {
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await handleFileUpload(files[0]);
    }
  }, []);

  const handleFileUpload = async (file) => {
    if (!file.type.startsWith("image/")) {
      onChange(null);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      onChange(null);
      return;
    }

    setIsUploading(true);
    await new Promise((resolve) => setTimeout(resolve, 500)); // simulate delay
    onChange(file);
    setIsUploading(false);
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (files?.length > 0) {
      await handleFileUpload(files[0]);
    }
  };

  const triggerFileSelect = () => {
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeFile = () => {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null; // ✅ Reset input element
    }
  };

  const truncateFileName = (name) => {
    const maxLength = 20;
    if (!name || name.length <= maxLength) return name;
    const extension = name.split(".").pop();
    return `${name.slice(0, maxLength - extension.length - 3)}...${extension}`;
  };

  const isFileValid = value instanceof File;

  return (
    <div className="w-full max-w-4xl mx-auto">
      <Label className="text-sm text-neutral-100">{placeholder}</Label>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {!isFileValid ? (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            "mt-[10px] relative border-2 border-dashed border-gray-300 rounded-lg p-8 text-center transition-colors",
            isDragActive && "border-purple-400 bg-purple-50",
            "hover:border-gray-400"
          )}
        >
          <div className="space-y-4">
            <p className="text-sm text-neutral-100">Drag & Drop file</p>
            <p className="text-neutral-100">Or</p>
            <p
              onClick={triggerFileSelect}
              className={cn(
                "inline-block bg-white border-2 border-[#2563EB] text-[#2563EB] hover:bg-purple-50 px-8 py-2 rounded-full font-medium cursor-pointer transition-colors select-none",
                isUploading && "opacity-50 cursor-not-allowed"
              )}
            >
              {isUploading ? "Uploading..." : "Upload"}
            </p>
            <p className="text-sm text-[#2563EB] mt-6">{fileFormat}</p>
          </div>
        </div>
      ) : (
        <div className="bg-purple-50 rounded-lg h-14 p-4 flex items-center justify-between mt-2">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#7B61FF"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path
                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                  fill="#EEF1F4"
                />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
            <span className="text-gray-800 font-medium">
              {truncateFileName(value?.name)}
            </span>
          </div>
          <button
            onClick={removeFile}
            className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-500 font-medium">{error}</p>
      )}
    </div>
  );
}

export function UploadsImagesWithPreview({
  placeholder,
  fileFormat,
  value,
  onChange,
  error,
  isCancel = true,
  setError, // ✅ Only this needed
}) {
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const isFileObject = value instanceof File;
  const isImageUrl = typeof value === "string";

  // Cleanup blob memory
  useEffect(() => {
    return () => {
      if (isFileObject) {
        URL.revokeObjectURL(value);
      }
    };
  }, [value]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await handleFileUpload(files[0]);
    }
  }, []);

  const handleFileUpload = async (file) => {
    const isInvalidType = !file.type.startsWith("image/");
    const isTooLarge = file.size > 5 * 1024 * 1024;

    if (isInvalidType || isTooLarge) {
      onChange(null); // Clear the current file
      if (setError) {
        const message = isInvalidType
          ? "Invalid file format. Only JPG, JPEG, and PNG images are supported."
          : "File exceeds size limit. Please upload a file smaller than 5MB.";
        setError(message); // ❗ Set the field error via form.setError
      }
      return;
    }

    setIsUploading(true);
    await new Promise((r) => setTimeout(r, 500));
    onChange(file);
    setIsUploading(false);
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (files?.length > 0) {
      await handleFileUpload(files[0]);
    }
  };

  const triggerFileSelect = () => {
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeFile = () => {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const truncateFileName = (name) => {
    const maxLength = 20;
    if (!name || name.length <= maxLength) return name;
    const extension = name.split(".").pop();
    return `${name.slice(0, maxLength - extension.length - 3)}...${extension}`;
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <Label className="text-sm text-neutral-100">{placeholder}</Label>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {isFileObject || isImageUrl ? (
        <div className="bg-[#FAF5FF] rounded-lg border mt-2 flex flex-col">
          {/* Image Preview */}
          <img
            src={isFileObject ? URL.createObjectURL(value) : value}
            alt="Preview"
            // className="w-full max-h-[20rem] h-auto object-cover rounded-md"
            className="aspect-square w-full max-h-[20rem] object-contain rounded-md bg-black"
          />

          {/* Bottom Section: File name + Actions */}
          <div className="flex justify-between items-center p-4">
            <p className="text-sm font-medium text-gray-800">
              {truncateFileName(isFileObject ? value.name : "thumbnail.jpg")}
            </p>

            {isCancel && (
              <div className="flex items-center gap-2">
                <p
                  onClick={triggerFileSelect}
                  className="px-3 py-1 text-sm text-neutral-0 cursor-pointer border border-neutral-0 rounded-md hover:bg-neutral-101 font-medium"
                >
                  Replace
                </p>
                <p
                  onClick={removeFile}
                  className="w-7 h-7 rounded-full bg-red-500 cursor-pointer hover:bg-red-600 flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-white" />
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            "mt-[10px] relative border-2 border-dashed border-gray-300 rounded-lg p-8 text-center transition-colors",
            isDragActive && "border-gray-400 bg-neutral-101",
            "hover:border-gray-400"
          )}
        >
          <div className="space-y-4">
            <p className="text-sm text-neutral-100">Drag & Drop file Or</p>
            {/* <p className="text-neutral-100">Or</p> */}
            <p
              onClick={triggerFileSelect}
              className={cn(
                "inline-block border border-[#83F1FF] text-[#83F1FF] hover:bg-neutral-111 px-10 py-2 rounded-full font-normal cursor-pointer transition-colors select-none",
                isUploading && "opacity-50 cursor-not-allowed"
              )}
            >
              {isUploading ? "Uploading..." : "Upload file"}
            </p>
            <p className="text-sm text-[#83F1FF] mt-6">{fileFormat}</p>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-500 font-medium">{error}</p>
      )}
    </div>
  );
}
